"use strict";

var constats = require('./constants');
var currencies = constats.currencies;
var currencySymbols = constats.symbols;
var abbreviations = constats.abbreviations;

/**
 * A more forgiving parseFloat
 * This is useful when consuming user-input
 * @param {number|string} number Either a formatted string (3,000) or integer (3000)
 * @returns {Number}
 */
exports.parseFloat = function dibsParsFloat(number) {
    return parseFloat(String(number).replace(/[^\d\.e-]/ig, '')) || 0;
};

/**
 * Round to the given precision
 * @param {number|string} number @see Number.parseFloat#number
 * @param {number} digits
 * @returns {number}
 */
exports.round = function round(number, digits) {
    var powed = Math.pow(10, digits);
    return Math.round(exports.parseFloat(number) * powed) / powed;
};

/**
 * Similar to PHP's number_format
 * @param number
 * @param {number} significantDigits The decimal place to round and fix a number to, Defaults to 0
 * @param {String} thousandsSeparator Defaults to ,
 * @param {String} decimalMark Defaults to .
 */
exports.format = function format(number, significantDigits, thousandsSeparator, decimalMark) {
    var suffix = '';
    var isNegative = number < 0;
    var bits;
    var i;

    if (isNegative) {
        number = number * -1;
    }

    significantDigits = significantDigits > -1 ? significantDigits : 0;
    thousandsSeparator = thousandsSeparator === undefined ? ',' : thousandsSeparator;
    decimalMark = decimalMark === undefined ? '.' : decimalMark;
    number = exports.round(number, significantDigits).toFixed(significantDigits);
    if (thousandsSeparator && number.match(/^\d{4}/)) {
        if (significantDigits) {
            suffix = number.substr(number.indexOf('.'));
            number = number.split('.')[0];
        }
        bits = number.split('');
        i = bits.length - 3;
        while (i > 0) {
            bits.splice(i, 0, thousandsSeparator);
            i -= 3;
        }
        number = bits.concat(suffix).join('');
    }
    if (decimalMark !== '.' && significantDigits) {
        number = number.replace(/\.(\d+)$/, decimalMark + '$1');
    }

    if (isNegative) {
        number = '-' + number;
    }

    return number;
};

/**
 *
 * @param {number} number The number to format
 * @param {object} [options] Formatting options
 * @param {boolean} [options.showDecimal] Show a decimal or not. Defaults to true
 * @param {string} [options.thousandsSeparator] What separates the thousands? Defaults to comma ","
 * @param {string} [options.decimalMark] What separates decimals? Defaults to period "."
 * @param {string} [options.abbreviation] Wheter to include an abbreviation or not
 * @param {boolean} [options.includeCurrencySymbol] Wheter to include a currency symbol or not
 * @param {string} [options.currencySymbol] Which currency symbol to use. Defaults to USD "$"
 * @returns {object}
 */
exports.localizedCashFormatObj = function (number, options) {
    options = Object.assign({
        includeCurrencySymbol: true
    }, options);
    var decimalsToShow = options.showDecimal === false ?  0 : 2;
    var thousandsSeparator = options.thousandsSeparator || ',';
    var decimalMark = options.decimalMark || '.';
    var currencySymbol = options.currencySymbol || '$';
    var abbreviation = options.abbreviation || '';
    var sign = '';

    number = exports.format(number, decimalsToShow, thousandsSeparator, decimalMark);
    currencySymbol = options.includeCurrencySymbol ? currencySymbol : '';

    // if it's negative we need to put the negative sign in front
    if (number.indexOf('-') !== -1) {
        sign = '-';
        number = number.replace('-', '');
    }

    return {
        sign: sign,
        currencySymbol: currencySymbol,
        number: number,
        abbreviation: abbreviation
    };
};

/**
 *
 * @param {number} number The number to format
 * @param {object} [options] Formatting options
 * @param {boolean} [options.showDecimal] Show a decimal or not. Defaults to true
 * @param {string} [options.thousandsSeparator] What separates the thousands? Defaults to comma ","
 * @param {string} [options.decimalMark] What separates decimals? Defaults to period "."
 * @param {string} [options.abbreviation] Wheter to include an abbreviation or not
 * @param {boolean} [options.includeCurrencySymbol] Wheter to include a currency symbol or not
 * @param {string} [options.currencySymbol] Which currency symbol to use. Defaults to USD "$"
 * @returns {string}
 */
exports.localizedCashFormat = function (number, options) {
    var localizedObject = exports.localizedCashFormatObj(number, options);
    var localizedNumber = localizedObject.number;
    var currencySymbol = localizedObject.currencySymbol;
    var abbreviation = localizedObject.abbreviation;
    var sign = localizedObject.sign;

    // if it's negative we need to put the negative sign in front
    if (sign) {
        sign = sign + ' ';
    }

    if (abbreviation.length) {
        abbreviation = ' ' + abbreviation;
    }

    if (options.formatNegativeAmount && sign) {
        return '(' + currencySymbol + localizedNumber + abbreviation + ')';
    }

    return sign + currencySymbol + localizedNumber + abbreviation;
};

/**
 * Converts a string containing numbers to an integer
 * @param  {string} amount
 * @return {number}
 */
exports.currencyToInteger = function (amount) {
    // Match all numbers and - sign
    var regexp = /-?[0-9]*/g;
    if (typeof amount === 'number') {
        return amount;
    } else if (typeof amount === 'string') {
        return parseInt(amount.match(regexp).join(''), 10);
    } else {
        return NaN;
    }
};

/**
 * Used internaly for intelligentFormat
 * @param  {number} The number to format
 * @param  {string}
 * @param  {object} passedOptions
 * @return {object}
 */
exports.getFormatOptions = function (amount, currency, passedOptions) {
    var options;
    var num = exports.parseFloat(amount);
    var currencyUppercase = (currency + '').toUpperCase();
    options = {
        // Only if there are decimals do we show them
        showDecimal : Math.ceil(num) !== Math.floor(num),
        thousandsSeparator : ',',
        decimalMark : '.',
        currencySymbol : currencySymbols.USD,
        abbreviation: abbreviations[currencyUppercase],
        formatNegativeAmount: false
    };

    switch (currencyUppercase) {
        case currencies.CURRENCY_GBP:
        case currencySymbols.GBP:
            options.currencySymbol = currencySymbols.GBP;
            break;
        case currencies.CURRENCY_CHF:
        case currencySymbols.CHF:
            options.includeCurrencySymbol = false;
            break;
        case currencies.CURRENCY_EUR:
        case currencySymbols.EUR:
            options.thousandsSeparator = '.';
            options.decimalMark = ',';
            options.currencySymbol = currencySymbols.EUR;
            break;
    }

    return Object.assign(options, passedOptions);
};



//////////////////
// WEBPACK FOOTER
// ./~/dibs-currency-helpers/util.js
// module id = 62
// module chunks = 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40 41 42 43 44 45 46 47 48 49 50 51 52 53 54 55 56